import { Badge, Button, colors, Stack, Typography } from '@ui-components/neo.tax';
import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';

import { ProviderLogo } from '../organisms/provider-logo';
import { DataSourcesTableItem, formatDateTime, getDataSourceTypeLabel } from '../utils';

/**
 * The Data Sources tables on both products, which differ in one thing only: which rows are a
 * "Delete" rather than a "Disconnect". A connection is disconnected and an upload is deleted, and
 * which item types are uploads is the product's to say — R&D has tax documents and ticketing files,
 * ASC has costs and payroll-month expenses.
 */
export const useDataSourcesTableColumns = ({
  canModify = true,
  deletableItemTypes,
  setItemToDelete,
  tableType = 'connection',
}: {
  /**
   * Whether to render the row's Delete/Disconnect action. False for viewers who cannot mutate the
   * source — the server rejects the mutation for them, so the button would only ever error.
   */
  canModify?: boolean;
  /** Item types whose action reads "Delete"; everything else reads "Disconnect". */
  deletableItemTypes: ReadonlySet<DataSourcesTableItem['itemType']>;
  setItemToDelete: (item: DataSourcesTableItem) => void;
  tableType?: 'connection' | 'file-upload' | 'payroll-file-upload' | 'ticketing-file-upload';
}): Column<DataSourcesTableItem>[] =>
  useMemo<Column<DataSourcesTableItem>[]>(
    () => [
      {
        id: 'displayName',
        accessor: (datum: DataSourcesTableItem): DataSourcesTableItem => datum,
        width: 380,
        Header: 'Name',
        Cell: ({
          value,
        }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem>>): JSX.Element => (
          <Stack direction="row" alignItems="center" gap={2} width={320} overflow="hidden">
            {value.provider ? <ProviderLogo provider={value.provider} size={24} /> : null}
            <Typography textOverflow="truncate" as="span" fontSize="xs">
              {value.title}
            </Typography>
          </Stack>
        ),
      },
      ...(tableType === 'connection'
        ? [
            {
              accessor: 'itemType' as const,
              width: 240,
              Header: 'Type',
              Cell: ({
                value,
              }: React.PropsWithChildren<
                CellProps<DataSourcesTableItem, DataSourcesTableItem['itemType']>
              >): JSX.Element => (
                <Typography fontSize="xs" color={colors.gray[900]}>
                  {getDataSourceTypeLabel(value)}
                </Typography>
              ),
            },
          ]
        : tableType !== 'payroll-file-upload'
          ? [
              {
                accessor: 'providerLabel' as const,
                width: 200,
                Header: 'Provider',
                Cell: ({
                  value,
                }: React.PropsWithChildren<
                  CellProps<DataSourcesTableItem, DataSourcesTableItem['providerLabel']>
                >): JSX.Element => (
                  <Typography fontSize="xs" color={colors.gray[900]}>
                    {value ?? '—'}
                  </Typography>
                ),
              },
            ]
          : []),
      ...(tableType === 'ticketing-file-upload'
        ? [
            {
              accessor: 'entity' as const,
              width: 160,
              Header: 'Entity',
              Cell: ({
                value,
              }: React.PropsWithChildren<
                CellProps<DataSourcesTableItem, DataSourcesTableItem['entity']>
              >): JSX.Element => (
                <Typography fontSize="xs" color={colors.gray[900]}>
                  {value ?? '—'}
                </Typography>
              ),
            },
          ]
        : []),
      {
        id: 'status',
        width: 140,
        Header: 'Status',
        accessor: (datum: DataSourcesTableItem): DataSourcesTableItem => datum,
        Cell: ({
          value,
        }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem>>): JSX.Element => {
          switch (value.status) {
            case 'success':
              return (
                <Badge dot={false} colorVariant={tableType === 'connection' ? 'blue' : 'green'}>
                  {tableType === 'connection' ? 'Completed' : 'Validated'}
                </Badge>
              );
            case 'failure':
              return (
                <Badge dot={false} colorVariant="red">
                  Failed
                </Badge>
              );
            case 'pending':
              return (
                <Badge dot={false} colorVariant="default">
                  Processing
                </Badge>
              );
            case 'validating':
              return (
                <Badge dot={false} colorVariant="default">
                  Validating
                </Badge>
              );
            case 'saved':
              return (
                <Badge dot={false} colorVariant="blue">
                  Saved
                </Badge>
              );
            default:
              return (
                <Typography fontSize="xs" fontWeight="medium" color={colors.gray[600]}>
                  ---
                </Typography>
              );
          }
        },
      },
      {
        accessor: 'originalSyncDate',
        width: 'auto',
        Header: tableType === 'connection' ? 'Connected On' : 'Uploaded on',
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<DataSourcesTableItem, DataSourcesTableItem['originalSyncDate']>
        >): JSX.Element => (
          <Typography fontSize="xs" color={colors.gray[900]}>
            {formatDateTime(value)}
          </Typography>
        ),
      },
      ...(canModify
        ? [
            {
              id: 'delete',
              width: 140,
              accessor: (datum: DataSourcesTableItem): DataSourcesTableItem => datum,
              Cell: ({
                value,
              }: React.PropsWithChildren<CellProps<DataSourcesTableItem, DataSourcesTableItem>>): JSX.Element => {
                const onClick = (): void => {
                  setItemToDelete(value);
                };
                return (
                  <Stack justifyContent="end" fullWidth>
                    {/* TODO: https://linear.app/neo-tax/issue/NEO-6616/add-support-for-deleting-a-timesheet-on-the-data-source-page */}
                    {value.itemType === 'timesheet' ||
                    value.itemType === 'github-email-mapping' ||
                    value.itemType === 'accounting-token' ||
                    value.itemType === 'payroll-token' ? (
                      <></>
                    ) : (
                      <Button onClick={onClick} size="xs" variant="action" rounded="regular">
                        {deletableItemTypes.has(value.itemType) ? 'Delete' : 'Disconnect'}
                      </Button>
                    )}
                  </Stack>
                );
              },
            } as Column<DataSourcesTableItem>,
          ]
        : []),
    ],
    [canModify, deletableItemTypes, setItemToDelete, tableType],
  );
